/**
 * Group therapy admin setup types (UI + mapper layer).
 * Aligned with docs/prd/Groups/PRD_GROUP_THERAPY.md (closed | open only).
 *
 * Template-owned (live from template): session HCS (auto-stamped), photo.
 * Screening is always required via generic ServiceRequest screening (no flag/HCS on Group).
 * Template-defaulted but cohort-owned once saved: enrollment mode, description.
 * Template series defaults (sessionCount, dayOfWeek) seed new cohort schedules.
 * Schedule uses startTime + endTime (duration derived in UI) and a single recurrence code.
 */

export type GroupEnrollmentMode = 'closed' | 'open';

/** UI/lifecycle status for therapy cohorts (`Group.active` only). */
export type GroupLifecycleStatus = 'active' | 'inactive';

/** @deprecated Legacy codes stored on existing Group resources — normalized on read. */
export type LegacyGroupEnrollmentMode =
  | 'closed-fixed-iteration'
  | 'open-rolling'
  | 'continuous'
  | 'single-event';

export type GroupSetupFormValues = {
  id?: string;
  /** Draft groups are inactive until activation checklist passes. */
  active: boolean;
  name: string;
  /**
   * Display-only; sourced from the Group Template photo via join.
   * Not written on Group.
   */
  imageUrl?: string;
  organizationId?: string;
  organizationName?: string;
  /** Defaults from template; editable and persisted on Group. */
  description?: string;
  /** Group Template HealthcareService reference (`GROUP_TEMPLATE_EXT`). */
  templateReference?: string;
  templateDisplay?: string;
  /**
   * Session HealthcareService — seeded from template for activation UI;
   * not written on Group (owned by template).
   */
  programReference?: string;
  programDisplay?: string;
  /**
   * Defaults from the Group Template; editable and persisted on Group.
   * When reading a Group with no stored mode, join fills from the template.
   */
  enrollmentMode: GroupEnrollmentMode;
  /** True when enrollment mode was present on the Group resource (not defaulted). */
  enrollmentModeStored?: boolean;
  primaryFacilitatorReference?: string;
  additionalFacilitatorReferences?: string[];
  facilitatorDisplays?: string[];
  startDate: string;
  endDate?: string;
  enrollmentLockDate?: string;
  daysOfWeek: string[];
  startTime: string;
  /** HH:mm end of session; duration is derived (UI default 18:00–19:00). */
  endTime: string;
  /** IANA timezone for local schedule interpretation. */
  timezone?: string;
  /**
   * Recurrence pattern.
   * `custom` uses sessionDates; weekly/biweekly use daysOfWeek.
   */
  recurrence?: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'custom';
  /** Explicit YYYY-MM-DD session dates when recurrence === 'custom'. */
  sessionDates?: string[];
  sessionCount?: number;
  minEnrollment: number;
  maxEnrollment: number;
  /** Cohort session modality (`SESSION_MODALITY_SYSTEM`). Required to activate. */
  modality?: 'in-person' | 'telehealth' | 'hybrid' | 'either';
  /** Site Location/{id} when modality is in-person (required) or hybrid. */
  locationReference?: string;
  locationDisplay?: string;
  /** Therapy-room / phone-booth Location id (bare id, calendar parity). */
  roomId?: string;
  roomDisplay?: string;
  /** Portable Device ids (bare ids, calendar parity). */
  deviceIds?: string[];
};

export function facilitatorReferencesFromForm(values: {
  primaryFacilitatorReference?: string;
  additionalFacilitatorReferences?: string[];
}): string[] {
  const primary = values.primaryFacilitatorReference?.trim();
  if (!primary) return [];
  const additional = (values.additionalFacilitatorReferences ?? []).filter(
    (id) => id && id !== primary,
  );
  return [primary, ...additional];
}

export function splitFacilitatorReferences(refs: string[]): {
  primaryFacilitatorReference?: string;
  additionalFacilitatorReferences: string[];
} {
  if (!refs.length) {
    return { primaryFacilitatorReference: undefined, additionalFacilitatorReferences: [] };
  }
  return {
    primaryFacilitatorReference: refs[0],
    additionalFacilitatorReferences: refs.slice(1),
  };
}

export type GroupListItem = GroupSetupFormValues & {
  enrolledCount: number;
  enrollmentLocked: boolean;
  /** Derived: active | draft (never activated) | inactive (archived/ended). */
  lifecycleStatus: GroupLifecycleStatus;
};

/** Group Therapy screening outcome codes stored on the Group screening note QuestionnaireResponse. */
export const GROUP_SCREENING_OUTCOME_CODES = [
  'appropriate-selected-group',
  'appropriate-different-group',
  'not-appropriate',
  'client-declined',
] as const;

export type GroupScreeningOutcomeCode = (typeof GROUP_SCREENING_OUTCOME_CODES)[number];

export const GROUP_SCREENING_OUTCOME_LABELS: Record<GroupScreeningOutcomeCode, string> = {
  'appropriate-selected-group': 'Appropriate for selected group',
  'appropriate-different-group': 'Appropriate for a different group',
  'not-appropriate': 'Not appropriate for group therapy',
  'client-declined': 'Client declined',
};
